import React, { useState, useEffect } from "react";
import { StaffSideMenu } from "../staff/StaffSideMenu";
import { useParams, useNavigate } from "react-router-dom";
import API_URL from "../../api/API";
import { useStaffAuthContext } from "../staff/useStaffAuthContext";

export const AthleteWarmupRoutine = () => {
  const { staff } = useStaffAuthContext();
  const { athlete_id } = useParams();
  const navigate = useNavigate();

  const [athleteName, setAthleteName] = useState("");
  const [phases, setPhases] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [selected, setSelected] = useState({
    phases: {},
    exercises: {},
  });
  const [date, setDate] = useState("");
  const [records, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

// State to trigger refetch after a workout is saved
const [isWorkoutSaved, setIsWorkoutSaved] = useState(false);


const fetchAllData = async () => {
  try {
    setIsLoading(true);
    setError("");

    // Step 1: Fetch Athlete Data
    const athleteResponse = await fetch(`${API_URL.GET_ATHLETE_BY_ID}${athlete_id}`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${staff.token}`,
      },
    });

    if (!athleteResponse.ok) {
      throw new Error("Failed to fetch athlete details");
    }

    const athleteData = await athleteResponse.json();
    setAthleteName(athleteData.firstname + " " + athleteData.lastname);


    // Step 3: Fetch Phases and Exercises
    const [phasesResponse, exercisesResponse, recordsResponse] = await Promise.all([
      fetch(API_URL.GET_PHASE_ALL_DATA, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${staff.token}`,
        },
      }),
      fetch(API_URL.GET_PHASE_EXERCISE_ALL_DATA, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${staff.token}`,
        },
      }),

      fetch(`${API_URL.GET_ATHLETE_WARMUP_ROUTINE}${athlete_id}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${staff.token}`,
        },
      }),
    ]);



    if (!phasesResponse.ok || !exercisesResponse.ok || !recordsResponse.ok) {
      throw new Error("Failed to fetch phases or exercises data");
    }

    const phasesData = await phasesResponse.json();
    const exercisesData = await exercisesResponse.json();
    const recordsData = await recordsResponse.json();

    // Flattening the response before setting state
    const flattenedRecords = Object.keys(recordsData).flatMap((date) =>
      recordsData[date].map((routine) => ({
        _id: routine._id,  // Assuming _id exists
        date: routine.date,
        exercises: routine.exercises,  // Or other data needed
      }))
    );

  setRecords(flattenedRecords); 

    setPhases(phasesData.phases);
    setExercises(exercisesData.exercises);
    setRecords(flattenedRecords);


    
  } catch (err) {
    console.error(err);
    setError(err.message || "Error fetching data");
  } finally {
    setIsLoading(false);
  }
};



  useEffect(() => {
    fetchAllData();
  }, [athlete_id, staff.token]);

  // Trigger refetch after saving a workout
  useEffect(() => {
      if (isWorkoutSaved) {
        fetchAllData();
        setIsWorkoutSaved(false); // Reset after fetching
      }
}, [isWorkoutSaved]); // Refetch after saving
  
  

  const handleSave = async () => {
    setError("");
    setSuccess("");

    const selectedExercises = exercises
      .filter((ex) => selected.exercises[ex._id])
      .map((ex) => ({
        exercise_id: ex._id,
        phase_id: ex.phase_id,
        title: ex.title,
      }));

    const data = {
      athlete_id,
      date,
      selectedExercises,
    };

    try {
      const response = await fetch(API_URL.POST_ATHLETE_WARMUP_ROUTINE, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${staff.token}`,
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error || "Failed to save data");
        return;
      }

      setSuccess("Warmup routine saved successfully!");
      setDate("");
      setSelected({ phases: {}, exercises: {} });
      setIsWorkoutSaved(true); // Mark that the workout was saved
    } catch (err) {
      setError(err.message || "Error saving data");
    }
  };


  const handleEdit = async (recordId) => {
    navigate('/athlete/warmup-routine/edit/' + recordId);
  }


  const handleDelete = async (recordId) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this record?");
    
    if (!confirmDelete) {
      return; // Exit if the user cancels the delete action
    }
    
    try {
      const response = await fetch(`${API_URL.DELETE_ATHLETE_WARMUP_ROUTINE}${recordId}`, {
        method: "DELETE",
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${staff.token}`,
        },
      });
  
      if (!response.ok) {
        setError("Failed to delete the record");
        return;
      }
  
      setRecords((prev) => prev.filter((record) => record._id !== recordId));
      setSuccess("Record deleted successfully");
    } catch (err) {
      setError(err.message || "Error deleting record");
    }
  };

  const togglePhase = (phaseId) => {
    const isSelected = !selected.phases[phaseId];
    const updatedExercises = { ...selected.exercises };

    exercises
      .filter((ex) => ex.phase_id === phaseId)
      .forEach((ex) => {
        updatedExercises[ex._id] = isSelected;
      });

    setSelected((prev) => ({
      phases: { ...prev.phases, [phaseId]: isSelected },
      exercises: updatedExercises,
    }));
  };

  const toggleExercise = (exerciseId, phaseId) => {
    const isSelected = !selected.exercises[exerciseId];
    const exercisesInPhase = exercises.filter((ex) => ex.phase_id === phaseId);
    const allChecked = exercisesInPhase.every((ex) => ex._id === exerciseId ? isSelected : selected.exercises[ex._id]);

    setSelected((prev) => ({
      phases: { ...prev.phases, [phaseId]: allChecked },
      exercises: { ...prev.exercises, [exerciseId]: isSelected },
    }));
  };

  return (
    <div className="flex h-screen">
      <StaffSideMenu />
  
      <div className="flex-1 p-6 bg-gray-100 pt-16">

      <div className="absolute top-4 right-4">
          <button
            onClick={() => navigate(-1)} // Go back to the previous page
            className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
          >
            Go Back
          </button>
        </div>

          <div className="flex">
          <div className="w-1/2 pr-4">
              <h2 className="text-xl font-bold mb-4">Warmup Routine Records</h2>
              <div className="max-h-[500px] overflow-y-auto">
                {isLoading ? (
                  <div className="text-center text-blue-500 font-bold text-lg">
                    Loading...
                  </div>
                ) : (
                  records ? (
                    records.map((record) => (
                      <div key={record._id} className="mb-4 p-3 bg-white rounded shadow">
                        <div className="flex justify-between">
                          <div>
                            <span className="font-bold">Date:</span>{" "}
                            {new Date(record.date).toLocaleDateString()}
                          </div>
                          <div className="flex space-x-2">
                            <button
                              className="text-blue-500"
                              onClick={() => handleEdit(record._id)}
                            >
                              Edit
                            </button>
                            <button
                              className="text-red-500"
                              onClick={() => handleDelete(record._id)}
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-gray-500">No records found</div>
                  )
                )}
              </div>
            </div>
  
            {/* Right: Form */}
            <div className="w-1/2 pl-4">
              {error && (
                <div className="bg-red-100 text-red-500 p-3 rounded mb-4">
                  {error}
                </div>
              )}
              {success && (
                <div className="bg-green-100 text-green-500 p-3 rounded mb-4">
                  {success}
                </div>
              )}
  
              <h1 className="text-2xl font-bold mb-4">
                Warmup Routine for: <span className=" text-green-500">{athleteName || "Athlete"}</span>
              </h1>
              <div className="bg-white p-4 rounded shadow">
                <label htmlFor="date" className="block mb-2 font-bold">
                  Select Date:
                </label>
                <input
                  type="date"
                  id="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="border p-2 rounded mb-4 w-full"
                />
                {phases.map((phase) => (
                  <div key={phase._id} className="mb-4">
                    <div className="flex items-center mb-2">
                      <input
                        type="checkbox"
                        checked={!!selected.phases[phase._id]}
                        onChange={() => togglePhase(phase._id)}
                        className="mr-2"
                      />
                      <span className="font-bold">{phase.title}</span>
                    </div>
                    <div className="pl-6">
                      {exercises
                        .filter((ex) => ex.phase_id === phase._id)
                        .map((ex) => (
                          <div key={ex._id} className="flex items-center mb-1">
                            <input
                              type="checkbox"
                              checked={!!selected.exercises[ex._id]}
                              onChange={() => toggleExercise(ex._id, phase._id)}
                              className="mr-2"
                            />
                            <span>{ex.title}</span>
                          </div>
                        ))}
                    </div>
                  </div>
                ))}
                <button
                  onClick={handleSave}
                  className="px-4 py-2 mt-4 bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
      </div>
    </div>
  );
};
